/**
 * Commodity Valuation Page (KAN-300)
 *
 * Layout mirrors the FX Forward Valuation page:
 *   - Header with system date (top-right)
 *   - Two tabs: Valuation | Market Data
 *   - Valuation tab: collapsible Execution section → Recent Batches table → Trade grid
 *   - Market Data tab: required prices with inline editing + save
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useAuth } from '../../../components/auth/AuthContext';
import SystemDateDisplay from '../../../components/common/SystemDateDisplay';
import { useSystemDate } from '../../../hooks/useSystemDate';
import {
  commodityValuationService,
  RequiredPrice,
  ValuationRunResult,
  ValuationTradeDetail,
  ValuationUpload,
} from '../../../services/api/commodityValuationService';
import { exportCommodityValuationToExcel } from '../../../utils/commodityValuationExcelExport';
import Toast from '../../../components/common/Toast';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../admin/Accounting.css';
import './CommodityValuation.css';

type CommodityValuationTab = 'valuation' | 'marketData';

/** Format number with locale-aware thousands separators */
function fmtNum(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined) return '';
  return v.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Format YYYY-MM-DD → DD/MM/YYYY */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

/** Format ISO datetime → DD/MM/YYYY HH:mm */
const formatDateTime = (isoStr: string): string => {
  if (!isoStr) return '\u2014';
  try {
    const d = new Date(isoStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoStr;
  }
};

const CommodityValuation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id || '';
  const systemDateResult = useSystemDate(clientId);

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // Tab state
  const [activeTab, setActiveTab] = useState<CommodityValuationTab>('valuation');

  // Execution section (collapsible)
  const [executionExpanded, setExecutionExpanded] = useState(false);
  const [valuationDate, setValuationDate] = useState('');
  const [pendingValuationDate, setPendingValuationDate] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<ValuationRunResult | null>(null);

  // Batches
  const [uploads, setUploads] = useState<ValuationUpload[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  // Trade grid (for selected batch)
  const [batchTrades, setBatchTrades] = useState<ValuationTradeDetail[]>([]);
  const [loadingBatch, setLoadingBatch] = useState(false);

  // Delete batch
  const [confirmDeleteBatch, setConfirmDeleteBatch] = useState<ValuationUpload | null>(null);
  const [deletingBatchId, setDeletingBatchId] = useState<string | null>(null);

  // Execution section — price requirements checklist
  const [execPriceReqs, setExecPriceReqs] = useState<RequiredPrice[]>([]);
  const [loadingExecPrices, setLoadingExecPrices] = useState(true);

  // Market Data tab state
  const [marketDataDate, setMarketDataDate] = useState('');
  const [pendingMarketDataDate, setPendingMarketDataDate] = useState('');
  const [requiredPrices, setRequiredPrices] = useState<RequiredPrice[]>([]);
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // ── Init valuation date from system date ──
  useEffect(() => {
    if (systemDateResult.systemDate && !valuationDate) {
      setValuationDate(systemDateResult.systemDate);
      setPendingValuationDate(systemDateResult.systemDate);
    }
    if (systemDateResult.systemDate && !marketDataDate) {
      setMarketDataDate(systemDateResult.systemDate);
      setPendingMarketDataDate(systemDateResult.systemDate);
    }
  }, [systemDateResult.systemDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load execution price requirements when valuation date changes ──
  const loadExecPriceReqs = useCallback(async () => {
    if (!clientId || !valuationDate) {
      setExecPriceReqs([]);
      // Only clear loading if system date already resolved (avoids stuck spinner)
      if (!systemDateResult.loading) {
        setLoadingExecPrices(false);
      }
      return;
    }
    setLoadingExecPrices(true);
    try {
      const resp = await commodityValuationService.getRequiredPrices(clientId, valuationDate);
      if (resp.success) {
        setExecPriceReqs(resp.data.requiredPrices);
      }
    } catch {
      // Non-critical — checklist will just be empty
    } finally {
      setLoadingExecPrices(false);
    }
  }, [clientId, valuationDate, systemDateResult.loading]);

  useEffect(() => { loadExecPriceReqs(); }, [loadExecPriceReqs]);

  // ── Auto-load prices when switching to Market Data tab or when date changes ──
  useEffect(() => {
    if (activeTab === 'marketData' && marketDataDate && clientId) {
      handleLoadPrices();
    }
  }, [activeTab, marketDataDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load uploads on mount ──
  const loadUploads = useCallback(async () => {
    if (!clientId) return;
    setLoadingUploads(true);
    try {
      const resp = await commodityValuationService.getUploads(clientId, 20);
      if (resp.success) {
        setUploads(resp.data || []);
      }
    } catch {
      // Non-critical
    } finally {
      setLoadingUploads(false);
    }
  }, [clientId]);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  // ── Load batch detail when selected ──
  const handleBatchSelect = useCallback(async (batchId: string) => {
    if (selectedBatchId === batchId) {
      setSelectedBatchId(null);
      setBatchTrades([]);
      return;
    }
    setSelectedBatchId(batchId);
    setLoadingBatch(true);
    try {
      const resp = await commodityValuationService.getBatch(clientId, batchId);
      if (resp.success) {
        setBatchTrades(resp.data.trades || []);
      }
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorLoadBatch'), type: 'error' });
    } finally {
      setLoadingBatch(false);
    }
  }, [clientId, selectedBatchId]);

  // ── Calculate ──
  const handleCalculate = useCallback(async () => {
    if (!clientId || !valuationDate) return;
    setCalculating(true);
    setLastRunResult(null);
    try {
      const resp = await commodityValuationService.calculate(clientId, valuationDate);
      const result = resp.data;
      if (result) {
        setLastRunResult(result);
        if (result.tradeCount > 0) {
          setToast({
            message: t('commodities.valuation.calculationComplete', { count: result.tradeCount }),
            type: 'success',
          });
          // Refresh uploads list and auto-select the new batch
          await loadUploads();
          setSelectedBatchId(result.uploadId);
          const batchResp = await commodityValuationService.getBatch(clientId, result.uploadId);
          if (batchResp.success) {
            setBatchTrades(batchResp.data.trades || []);
          }
        } else if (result.errors?.length) {
          setToast({ message: result.errors[0]?.error || 'Calculation failed', type: 'error' });
        } else {
          setToast({ message: 'No trades valued', type: 'info' });
        }
      } else {
        setToast({ message: resp.message || 'No trades valued', type: 'info' });
      }
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorCalculation'), type: 'error' });
    } finally {
      setCalculating(false);
    }
  }, [clientId, valuationDate, t, loadUploads]);

  // ── Delete batch ──
  const handleDeleteBatch = useCallback(async () => {
    if (!confirmDeleteBatch || !clientId) return;
    const batchId = confirmDeleteBatch.id;
    setConfirmDeleteBatch(null);
    setDeletingBatchId(batchId);
    try {
      await commodityValuationService.deleteBatch(clientId, batchId);
      setToast({ message: t('commodities.valuation.deleteSuccess'), type: 'success' });
      if (selectedBatchId === batchId) {
        setSelectedBatchId(null);
        setBatchTrades([]);
      }
      await loadUploads();
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorDeleteBatch'), type: 'error' });
    } finally {
      setDeletingBatchId(null);
    }
  }, [confirmDeleteBatch, clientId, selectedBatchId, loadUploads, t]);

  // ── Load required prices (Market Data tab) ──
  const handleLoadPrices = useCallback(async () => {
    if (!clientId || !marketDataDate) return;
    try {
      const resp = await commodityValuationService.getRequiredPrices(clientId, marketDataDate);
      if (resp.success) {
        setRequiredPrices(resp.data.requiredPrices);
        const edits: Record<string, string> = {};
        for (const rp of resp.data.requiredPrices) {
          if (rp.price !== null) {
            edits[rp.fixingReference] = String(rp.price);
          }
        }
        setPriceEdits(edits);
      } else {
        setToast({ message: t('commodities.valuation.errorLoadPrices'), type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorLoadPrices'), type: 'error' });
    }
  }, [clientId, marketDataDate]);

  // ── Save prices ──
  const handleSavePrices = useCallback(async () => {
    if (!clientId || !marketDataDate) return;
    const pricesToSave = requiredPrices
      .filter(rp => {
        const edited = priceEdits[rp.fixingReference];
        return edited !== undefined && edited.trim() !== '' && !isNaN(parseFloat(edited));
      })
      .map(rp => ({
        fixingReference: rp.fixingReference,
        price: parseFloat(priceEdits[rp.fixingReference]),
        currency: rp.currency || 'USD',
      }));

    if (pricesToSave.length === 0) {
      setToast({ message: t('commodities.valuation.noPricesToSave'), type: 'info' });
      return;
    }

    setSaving(true);
    try {
      const resp = await commodityValuationService.savePrices(clientId, marketDataDate, pricesToSave);
      if (resp.success) {
        setToast({
          message: t('commodities.valuation.pricesSaved', { count: resp.data.savedCount }),
          type: 'success',
        });
        await handleLoadPrices();
        // Refresh execution checklist if dates match
        if (marketDataDate === valuationDate) {
          loadExecPriceReqs();
        }
      } else {
        setToast({ message: t('commodities.valuation.errorSavePrices'), type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorSavePrices'), type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [clientId, marketDataDate, valuationDate, requiredPrices, priceEdits, handleLoadPrices, loadExecPriceReqs, t]);

  // ── Delete a single price ──
  const handleDeletePrice = useCallback(async (fixingReference: string) => {
    if (!clientId || !marketDataDate) return;
    try {
      const resp = await commodityValuationService.deletePrice(clientId, fixingReference, marketDataDate);
      if (resp.success) {
        // Clear the edit field and reload
        setPriceEdits(prev => {
          const next = { ...prev };
          delete next[fixingReference];
          return next;
        });
        await handleLoadPrices();
        if (marketDataDate === valuationDate) {
          loadExecPriceReqs();
        }
      } else {
        setToast({ message: t('commodities.valuation.errorDeletePrice'), type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: t('commodities.valuation.errorDeletePrice'), type: 'error' });
    }
  }, [clientId, marketDataDate, valuationDate, handleLoadPrices, loadExecPriceReqs, t]);

  // ── Derived state ──
  const hasUnsavedEdits = requiredPrices.some(rp => {
    const edited = priceEdits[rp.fixingReference];
    if (!edited) return false;
    if (rp.price === null) return edited.trim() !== '';
    return parseFloat(edited) !== rp.price;
  });

  // Total P&L for selected batch
  const batchTotalPnl = batchTrades.reduce((sum, t) => sum + (t.unrealisedPnl || 0), 0);

  // ── AG Grid column definitions ──
  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('commodities.valuation.tradeNumber'),
      field: 'tradeNumber',
      minWidth: 90,
      pinned: 'left' as const,
      sort: 'asc' as const,
    },
    {
      headerName: t('commodities.valuation.entity'),
      field: 'ourParty',
      minWidth: 120,
    },
    {
      headerName: t('commodities.valuation.product'),
      field: 'productType',
      minWidth: 90,
    },
    {
      headerName: t('commodities.valuation.underlying'),
      field: 'underlyingName',
      minWidth: 140,
    },
    {
      headerName: t('commodities.valuation.direction'),
      field: 'direction',
      minWidth: 80,
      cellStyle: (params: any) => ({
        color: params.value === 'Buy' ? '#10b981' : '#ef4444',
        fontWeight: 600,
      }),
    },
    {
      headerName: t('commodities.valuation.counterparty'),
      field: 'counterpartyName',
      minWidth: 140,
    },
    {
      headerName: t('commodities.valuation.lots'),
      field: 'lots',
      minWidth: 70,
      type: 'numericColumn',
      valueFormatter: (params: any) => params.value != null ? fmtNum(params.value, 0) : '',
    },
    {
      headerName: t('commodities.valuation.entryPrice'),
      field: 'entryPrice',
      minWidth: 100,
      type: 'numericColumn',
      valueFormatter: (params: any) => params.value != null ? fmtNum(params.value) : '',
    },
    {
      headerName: t('commodities.valuation.quantity'),
      field: 'quantity',
      minWidth: 100,
      type: 'numericColumn',
      valueFormatter: (params: any) => params.value != null ? fmtNum(params.value, 4) : '',
    },
    {
      headerName: t('commodities.valuation.fixingRef'),
      field: 'fixingReference',
      minWidth: 90,
    },
    {
      headerName: t('commodities.valuation.marketPrice'),
      field: 'marketPrice',
      minWidth: 100,
      type: 'numericColumn',
      valueFormatter: (params: any) => params.value != null ? fmtNum(params.value) : '',
    },
    {
      headerName: t('commodities.valuation.pnl'),
      field: 'unrealisedPnl',
      minWidth: 130,
      type: 'numericColumn',
      valueFormatter: (params: any) => params.value != null ? fmtNum(params.value) : '',
      cellStyle: (params: any) => ({
        color: (params.value || 0) >= 0 ? '#10b981' : '#ef4444',
        fontWeight: 600,
        fontFamily: 'monospace',
      }),
    },
    {
      headerName: t('commodities.valuation.currency'),
      field: 'currency',
      minWidth: 70,
    },
    {
      headerName: t('commodities.valuation.maturityDate', 'Maturity Date'),
      field: 'maturityDate',
      minWidth: 100,
      valueFormatter: (params: any) => params.value ? formatDate(params.value) : '',
    },
  ], [t]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    filter: true,
  }), []);

  const onGridFirstDataRendered = useCallback((params: any) => {
    params.api.autoSizeAllColumns();
  }, []);

  // ── Full-page loading gate ──
  // Block all content until the system date AND the first price-requirements
  // fetch have completed.  This prevents the "bit by bit" flickering the user
  // experienced in production.
  const initialLoading = systemDateResult.loading || loadingExecPrices;

  // ========== Render ==========

  return (
    <div className="accounting-page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>{t('commodities.valuation.title')}</h1>
        <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
      </div>

      {initialLoading ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '4rem 1rem', gap: '1rem',
        }}>
          <div className="spinner" style={{ width: '32px', height: '32px', borderWidth: '3px' }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            {t('common.loading')}...
          </span>
        </div>
      ) : (
      <>

      {/* Top-level tabs */}
      <div className="accounting-tabs">
        <button
          className={`tab-button ${activeTab === 'valuation' ? 'active' : ''}`}
          onClick={() => setActiveTab('valuation')}
        >
          {t('commodities.valuation.tabValuation')}
        </button>
        <button
          className={`tab-button ${activeTab === 'marketData' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketData')}
        >
          {t('commodities.valuation.tabMarketData')}
        </button>
      </div>

      {/* Tab content */}
      <div className="tab-content">

        {/* ════════════════════════════════════════════ */}
        {/* VALUATION TAB                               */}
        {/* ════════════════════════════════════════════ */}
        {activeTab === 'valuation' && (
          <div>
            {/* Calculating overlay */}
            {calculating && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem', marginBottom: '1rem',
                background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: '8px', gap: '1rem',
              }}>
                <div className="spinner" style={{ width: '24px', height: '24px', borderWidth: '2px' }} />
                <span style={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 600 }}>
                  {t('commodities.valuation.calculating')}
                </span>
              </div>
            )}

            {/* Execution Section (collapsible) */}
            <div className="config-section" style={{ marginBottom: '1rem' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: executionExpanded ? '0.75rem' : 0 }}
                onClick={() => setExecutionExpanded(prev => !prev)}
              >
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', width: '12px', flexShrink: 0 }}>
                  {executionExpanded ? '\u25BC' : '\u25B6'}
                </span>
                <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
                  {t('commodities.valuation.executionSection')}
                </h3>
              </div>

              {executionExpanded && (
                <div>
                  {/* Date picker */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t('commodities.valuation.valuationDate')}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="date"
                        value={pendingValuationDate}
                        onChange={e => setPendingValuationDate(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && pendingValuationDate && pendingValuationDate !== valuationDate) setValuationDate(pendingValuationDate); }}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          padding: '0.4rem 0.6rem',
                          color: 'var(--text-primary)',
                          fontSize: '0.85rem',
                          width: 'fit-content',
                        }}
                      />
                      {pendingValuationDate && pendingValuationDate !== valuationDate && (
                        <button
                          onClick={() => setValuationDate(pendingValuationDate)}
                          style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            color: '#e2e8f0',
                            padding: '2px 10px',
                            fontSize: '12px',
                            height: '28px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          OK
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Data requirements checklist + Calculate button */}
                  <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>

                    {/* Requirements list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: '180px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.15rem' }}>
                        {t('commodities.valuation.requirementsTitle')}
                        {valuationDate && (
                          <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', marginLeft: '0.3rem', fontFamily: 'monospace' }}>
                            {t('commodities.valuation.requirementsForDate', { date: formatDate(valuationDate) })}
                          </span>
                        )}
                      </span>
                      {loadingExecPrices ? (
                        <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', marginTop: '0.25rem' }} />
                      ) : !valuationDate ? (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{'\u2014'}</span>
                      ) : execPriceReqs.length === 0 ? (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          {t('commodities.valuation.noPricesRequired')}
                        </span>
                      ) : (
                        execPriceReqs.map(rp => (
                          <div key={rp.fixingReference} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{'\u2022'}</span>
                            <span style={{ color: rp.available ? 'var(--text-secondary)' : 'var(--text-primary)', flex: 1 }}>
                              {rp.label || rp.fixingReference}
                              <span style={{ fontFamily: 'monospace', marginLeft: '0.3rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                ({rp.fixingReference})
                              </span>
                            </span>
                            <span style={{ fontWeight: 700, fontSize: '0.75rem', color: rp.available ? '#2ecc71' : '#a07840', minWidth: '1rem', textAlign: 'center' }}>
                              {rp.available ? '\u2713' : '\u2717'}
                            </span>
                            {!rp.available && (
                              <button
                                type="button"
                                onClick={() => { setMarketDataDate(valuationDate); setPendingMarketDataDate(valuationDate); setActiveTab('marketData'); }}
                                style={{
                                  fontSize: '0.72rem',
                                  padding: '0.1rem 0.5rem',
                                  border: '1px solid #a07840',
                                  borderRadius: '4px',
                                  background: 'transparent',
                                  color: '#a07840',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('commodities.valuation.updateButton')}
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Calculate button — pushed to right */}
                    <div style={{ marginLeft: 'auto' }}>
                      {(() => {
                        const missingPrices = execPriceReqs.some(rp => !rp.available);
                        const isDisabled = calculating || !valuationDate || missingPrices || loadingExecPrices;
                        return (
                          <button
                            className="cv-btn cv-btn-primary"
                            onClick={handleCalculate}
                            disabled={isDisabled}
                            style={{ opacity: missingPrices ? 0.5 : undefined }}
                          >
                            {calculating ? t('commodities.valuation.calculating') : t('commodities.valuation.calculateMtm')}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Last run result (inline feedback) */}
                  {lastRunResult && lastRunResult.tradeCount > 0 && (
                    <div style={{
                      display: 'flex', gap: '1.5rem', flexWrap: 'wrap',
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(16, 185, 129, 0.06)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                    }}>
                      <span>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('commodities.valuation.tradesValued')}: </span>
                        <strong>{lastRunResult.tradeCount}</strong>
                      </span>
                      <span>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('commodities.valuation.totalPnl')}: </span>
                        <strong style={{
                          fontFamily: 'monospace',
                          color: lastRunResult.totalUnrealisedPnl >= 0 ? '#10b981' : '#ef4444',
                        }}>
                          {fmtNum(lastRunResult.totalUnrealisedPnl)}
                        </strong>
                      </span>
                      <span>
                        <span style={{ color: 'var(--text-secondary)' }}>{t('commodities.valuation.processingTime')}: </span>
                        <strong>{lastRunResult.processingTimeSeconds}s</strong>
                      </span>
                    </div>
                  )}

                  {/* Error feedback */}
                  {lastRunResult && lastRunResult.errors?.length > 0 && lastRunResult.tradeCount === 0 && (
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(239, 68, 68, 0.06)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#ef4444',
                    }}>
                      {lastRunResult.errors.map((err, i) => (
                        <div key={i}>{err.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Valuation Results heading ── */}
            <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('commodities.valuation.valuationResults')}
            </h3>

            {/* Recent Batches table */}
            <div className="config-section" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
                  {t('commodities.valuation.recentBatches')}
                </h3>
                <span style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                }}>
                  {uploads.length}
                </span>
              </div>

              {loadingUploads ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
                  {t('common.loading')}...
                </div>
              ) : uploads.length > 0 ? (
                <div className="accounts-table-container">
                  <table className="accounts-table">
                    <thead>
                      <tr>
                        <th>{t('commodities.valuation.batchId')}</th>
                        <th>{t('commodities.valuation.batchValDate')}</th>
                        <th>{t('commodities.valuation.batchTrades')}</th>
                        <th>{t('commodities.valuation.batchPnl')}</th>
                        <th>{t('commodities.valuation.batchPrices')}</th>
                        <th>{t('commodities.valuation.batchStatus')}</th>
                        <th>{t('commodities.valuation.batchCreated')}</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploads.map(batch => (
                        <tr
                          key={batch.id}
                          className={selectedBatchId === batch.id ? 'batch-row-selected' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleBatchSelect(batch.id)}
                        >
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {batch.id ? batch.id.slice(0, 8) : '-'}
                          </td>
                          <td>{formatDate(batch.uploadDate)}</td>
                          <td>{batch.tradeCount}</td>
                          <td>
                            <span style={{
                              fontFamily: 'monospace',
                              color: batch.totalUnrealisedPnl >= 0 ? '#10b981' : '#ef4444',
                            }}>
                              {fmtNum(batch.totalUnrealisedPnl)}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            {batch.pricesUsed
                              ? Object.entries(batch.pricesUsed).map(([ref, price]) => `${ref}: ${price}`).join(', ')
                              : '\u2014'}
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: batch.status === 'complete'
                                ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                              color: batch.status === 'complete' ? '#10b981' : '#ef4444',
                            }}>
                              {batch.status === 'complete'
                                ? t('commodities.valuation.statusComplete')
                                : batch.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.8rem' }}>{formatDateTime(batch.createdAt)}</td>
                          <td>
                            <button
                              className="btn-icon btn-danger"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteBatch(batch); }}
                              disabled={deletingBatchId === batch.id}
                              title={t('common.delete')}
                              style={{ fontSize: '0.8rem' }}
                            >
                              {deletingBatchId === batch.id ? '...' : '\u2715'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
                  {t('commodities.valuation.noBatches')}
                </div>
              )}
            </div>

            {/* Trade grid for selected batch */}
            {loadingBatch ? (
              <div className="config-section" style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{
                  width: 24, height: 24, margin: '0 auto 0.75rem',
                  border: '2px solid var(--border-color)',
                  borderTopColor: 'var(--accent-blue, #3b82f6)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('commodities.valuation.loadingBatch')}
                </span>
              </div>
            ) : selectedBatchId && batchTrades.length > 0 ? (
              <div>
                {/* Batch summary header + export button */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'rgba(59, 130, 246, 0.06)',
                  border: '1px solid #333',
                  borderLeft: '3px solid #3b82f6',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)',
                }}>
                  <span>{formatDate(batchTrades[0]?.valuationDate || '')}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                    ({batchTrades.length} {batchTrades.length === 1 ? 'trade' : 'trades'})
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem', fontSize: '0.8rem', fontWeight: 400, alignItems: 'center' }}>
                    <span>
                      <span style={{ color: 'var(--text-secondary)' }}>{t('commodities.valuation.totalPnl')}: </span>
                      <strong style={{
                        fontFamily: 'monospace',
                        color: batchTotalPnl >= 0 ? '#10b981' : '#ef4444',
                      }}>
                        {fmtNum(batchTotalPnl)}
                      </strong>
                    </span>
                    {/* Excel export */}
                    <span
                      role="button"
                      title={t('commodities.valuation.exportExcel')}
                      onClick={() => exportCommodityValuationToExcel(batchTrades, batchTrades[0]?.valuationDate || '')}
                      style={{
                        cursor: 'pointer',
                        padding: '0.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '0.5rem',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ color: '#28a745' }}>
                        <path d="M14 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM5.5 13H3V3h2.5v10zm4.5 0H6.5V3H10v10zm4 0h-3V3h3v10z"/>
                      </svg>
                    </span>
                  </div>
                </div>

                {/* AG Grid */}
                <div style={{
                  background: '#1a1a1a',
                  border: '1px solid #333',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '0.25rem',
                }}>
                  <div className="ag-theme-alpine-dark" style={{
                    width: '100%',
                    height: `${Math.min(Math.max(batchTrades.length * 42 + 56, 200), 600)}px`,
                  }}>
                    <AgGridReact
                      localeText={agGridLocale}
                      rowData={batchTrades}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      getRowId={(params) => `batch-${params.data.tradeId || params.data.id}`}
                      domLayout="normal"
                      animateRows={false}
                      onFirstDataRendered={onGridFirstDataRendered}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* ════════════════════════════════════════════ */}
        {/* MARKET DATA TAB                             */}
        {/* ════════════════════════════════════════════ */}
        {activeTab === 'marketData' && (
          <div>
            {/* Date picker + Load */}
            <div className="config-section" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {t('commodities.valuation.valuationDate')}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="date"
                      value={pendingMarketDataDate}
                      onChange={e => setPendingMarketDataDate(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && pendingMarketDataDate && pendingMarketDataDate !== marketDataDate) {
                          setMarketDataDate(pendingMarketDataDate);
                          setRequiredPrices([]);
                          setPriceEdits({});
                        }
                      }}
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.4rem 0.6rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                      }}
                    />
                    {pendingMarketDataDate && pendingMarketDataDate !== marketDataDate && (
                      <button
                        onClick={() => {
                          setMarketDataDate(pendingMarketDataDate);
                          setRequiredPrices([]);
                          setPriceEdits({});
                        }}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          color: '#e2e8f0',
                          padding: '2px 10px',
                          fontSize: '12px',
                          height: '28px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        OK
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Required prices table */}
            {requiredPrices.length > 0 && (
              <div className="config-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
                    {t('commodities.valuation.requiredPrices')}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={`cv-btn ${hasUnsavedEdits ? 'cv-btn-primary' : 'cv-btn-secondary'}`}
                      onClick={handleSavePrices}
                      disabled={saving || !hasUnsavedEdits}
                    >
                      {saving ? t('commodities.valuation.saving') : t('commodities.valuation.savePrices')}
                    </button>
                  </div>
                </div>

                <div className="accounts-table-container">
                  <table className="accounts-table">
                    <thead>
                      <tr>
                        <th>{t('commodities.valuation.date', 'Date')}</th>
                        <th>{t('commodities.valuation.fixingRef')}</th>
                        <th>{t('commodities.valuation.description')}</th>
                        <th style={{ textAlign: 'right' }}>{t('commodities.valuation.trades')}</th>
                        <th style={{ textAlign: 'right' }}>{t('commodities.valuation.price')}</th>
                        <th>{t('commodities.valuation.currency')}</th>
                        <th style={{ textAlign: 'center' }}>{t('commodities.valuation.status')}</th>
                        <th style={{ width: '36px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {requiredPrices.map(rp => {
                        const editValue = priceEdits[rp.fixingReference] ?? '';
                        return (
                          <tr key={rp.fixingReference}>
                            <td style={{ fontSize: '0.85rem' }}>{formatDate(marketDataDate)}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{rp.fixingReference}</td>
                            <td>{rp.label}</td>
                            <td style={{ textAlign: 'right' }}>{rp.tradeCount}</td>
                            <td style={{ textAlign: 'right' }}>
                              <input
                                type="text"
                                className="cv-price-input"
                                value={editValue}
                                onChange={e => setPriceEdits(prev => ({
                                  ...prev,
                                  [rp.fixingReference]: e.target.value,
                                }))}
                                placeholder="0.00"
                              />
                            </td>
                            <td>{rp.currency}</td>
                            <td style={{ textAlign: 'center' }}>
                              {rp.available ? (
                                <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem' }}>&#10003;</span>
                              ) : editValue && !isNaN(parseFloat(editValue)) ? (
                                <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.9rem' }}>&#9679;</span>
                              ) : (
                                <span style={{ color: '#ef4444', fontWeight: 700, fontSize: '0.9rem' }}>&#10007;</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center', padding: '0 2px' }}>
                              {rp.available && (
                                <button
                                  onClick={() => handleDeletePrice(rp.fixingReference)}
                                  title={t('commodities.valuation.deletePrice', 'Delete price')}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-tertiary, #64748b)', fontSize: '0.85rem',
                                    padding: '2px 4px', borderRadius: '4px', lineHeight: 1,
                                  }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary, #64748b)')}
                                >
                                  &#10005;
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Batch Confirmation Modal */}
      {confirmDeleteBatch && (
        <div
          onClick={() => setConfirmDeleteBatch(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('commodities.valuation.deleteTitle')}
            </h3>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('commodities.valuation.deleteMessage', {
                date: formatDate(confirmDeleteBatch.uploadDate),
                count: confirmDeleteBatch.tradeCount,
              })}
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('commodities.valuation.deleteDetail')}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteBatch(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteBatch}
                disabled={!!deletingBatchId}
                style={{
                  background: '#ef5350',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: deletingBatchId ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  opacity: deletingBatchId ? 0.5 : 1,
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {/* Toast */}
      <Toast
        message={toast?.message || ''}
        type={toast?.type}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />
    </div>
  );
};

export default CommodityValuation;
